import { Box, Button, Flex, Text as ChakraText } from "~/foundation/Chakra";
import React, { type FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useNavigationType } from "react-router";
import { type ApiResponse } from "~/foundation/Api/http";
import { type ISearchResultItemModel, type SearchFilterModel, type SearchResponseModel } from "./generated-types";
import { Icon } from "~/foundation/Components/Icon";
import { useTranslation } from "~/foundation/Dictionary";
import { type SearchDictionary } from "./dictionary";
import { type ItemReference, useSitecoreContext } from "~/foundation/Jss";
import { type Tag, type TopicTagFilter } from "~/foundation/SitecoreContent/generated-types";
import { SearchContext, type SearchQueryString } from "./SearchProvider";

type SearchResultProps = {
	service: (filterSearchModel: SearchFilterModel) => Promise<ApiResponse<SearchResponseModel>>;
	Loading: React.ReactNode;
	render: (results: ISearchResultItemModel[] | null, totalResultsCount: number) => React.ReactNode;
	defaultPageSize?: number;
	onFetchEnd?: (fetchData: SearchFilterModel) => void;
}

export const GenericSearchResult: FC<SearchResultProps> = ({ render, Loading, service, defaultPageSize = 10, onFetchEnd }) => {
	const navigate = useNavigate();
	const navigationType = useNavigationType();

	const [t] = useTranslation<SearchDictionary>();
	const { sitecoreContext } = useSitecoreContext<TopicTagFilter>();
	const prefilledTopicTag: ItemReference<Tag>[] = sitecoreContext.route?.fields?.selectTopicTags ?? [];

	const [loading, setLoading] = useState<boolean>(true);
	const [results, setResults] = useState<ISearchResultItemModel[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { facets, setTotalResultsCounts, totalResultsCount } = useContext(SearchContext);
	const [page, setPage] = useState<number>(parseInt(facets.page || "1"));

	const prevFacetsRef = useRef<SearchQueryString | null>(null);

	let pageSize: number;

	useEffect(() => {
		const initialPage = parseInt(facets.page || "1");
		pageSize = defaultPageSize * initialPage;
	}, [])

	useEffect(() => {
		setError(null);
		setLoading(true);

		const apiQueryStrings: SearchFilterModel = {
			query: facets.query?.replace(/[&\/\\#,+()$~%.'";:*?<>{}]/g, ''),
			page: pageSize > defaultPageSize ? 0 : page - 1,
			pageSize: pageSize ?? defaultPageSize,
			multisite: facets.multisite === "true"
		}

		if (prefilledTopicTag.length > 0) {
			apiQueryStrings.topics = prefilledTopicTag.map(x => x.name).join("|");
		} else {
			apiQueryStrings.topics = facets.topics;
			apiQueryStrings.pubstart = facets.pubstart;
			apiQueryStrings.pubend = facets.pubend;
			apiQueryStrings.sort = facets.sort ? `${facets.sort}|true` : undefined;
		}

		let tempResults = results;
		const tempFacets = { ...facets };

		delete prevFacetsRef.current?.page;
		delete tempFacets.page;

		if (
			navigationType === "POP" ||
			JSON.stringify(prevFacetsRef.current) !== JSON.stringify(tempFacets)
		) {
			apiQueryStrings.pageSize = defaultPageSize;
			apiQueryStrings.page = 0;

			setPage(apiQueryStrings.page + 1);
			setResults([]);
			tempResults = [];
		}

		service(apiQueryStrings)
			.then(response => {
				setLoading(false);
				if (response && response.statusCode === 200 && response.data) {
					if (Array.isArray(response.data.results)) {
						if (page === 1) {
							setResults(response.data.results);
						} else {
							const newResults = (tempResults ?? []).concat(response.data.results);
							setResults(newResults);
						}

						setTotalResultsCounts(response.data.totalResults);
						onFetchEnd?.(apiQueryStrings);
					}
				}
				pageSize = defaultPageSize;
			})
			.catch(() => {
				setLoading(false);
				setError("An error occurred getting search results");
			});

		prevFacetsRef.current = facets;

	}, [facets, page])

	const updatePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();

		const newPage = page + 1;
		setPage(newPage);

		const urlParams = new URLSearchParams(location.search);
		urlParams.set("page", newPage.toString());

		navigate({
			search: decodeURIComponent(urlParams.toString())
		});
	}

	return (
		<Box pt={[12, null, null, 16]}>
			<div>
				{results?.length === 0 && !loading && (
					<Box textAlign="center">
						<ChakraText
							size="xs"
							fontSize="lg" //override h6 mobile to match h6 desktop
							lineHeight="1.333" //override h6 mobile to match h6 desktop
						>
							- {t("search.noResults")} -
						</ChakraText>
					</Box>
				)}
				{render(results, totalResultsCount)}
			</div>
			<Flex justifyContent="center">
				{error
					? <div>{error}</div>
					: loading
						? <>{Loading}</>
						: (results?.length ?? 0) < totalResultsCount && (
							<Button
								as="a"
								href={`${sitecoreContext.custom.absoluteUrl}${(sitecoreContext.custom.absoluteUrl.indexOf("?") > -1 ? "&" : "?")}page=${page + 1}`}
								onClick={(event) => updatePage(event)}
								color="primary.aramcoLinkBlue">
								{t("search.loadMore")}
								<Flex alignItems="center" justifyContent="center" w="2.8125rem" h="2.8125rem" ms="1rem" border="2px solid" borderColor="primary.aramcoLinkBlue" borderRadius="50%">
									<Icon variant="ArrowDown" w="icons.md" color="primary.aramcoLinkBlue" />
								</Flex>
							</Button>
						)
				}
			</Flex>
		</Box>
	);
}